// P0-10 mock fixtures. Fake data for clickable mockups. Not real; nothing here
// touches the server. Deliberately uses obviously-fake names so staff can
// tell the mocks apart from the real app during feedback sessions.

export type Staff = {
  id: string;
  name: string;
  role: "Associate" | "Manager" | "Owner" | "I&R" | "Media" | "Instructor";
  initials: string;
  device?: string;
};

export const staff: Staff[] = [
  { id: "s-jake", name: "Jake M.", role: "Associate", initials: "JM", device: "Floor iPad 2" },
  { id: "s-elliott", name: "Elliott R.", role: "Manager", initials: "ER", device: "Counter 1" },
  { id: "s-ben", name: "Ben K.", role: "Owner", initials: "BK", device: "Back Office" },
  { id: "s-sarah", name: "Sarah T.", role: "Associate", initials: "ST", device: "Floor iPad 1" },
  { id: "s-max", name: "Max P.", role: "I&R", initials: "MP", device: "Receiving PC" },
];

export type Customer = {
  id: string;
  name: string;
  phone: string;
  email: string;
  address: string;
  hasPermit?: "carry" | "transferee" | null;
  permitExpires?: string;
  consents: { transactional: boolean; marketing: boolean };
};

export const customers: Customer[] = [
  {
    id: "c-1",
    name: "Fake Name One",
    phone: "(763) 555-0100",
    email: "one@example.test",
    address: "100 Example St, Maple Grove, MN 55369",
    hasPermit: "carry",
    permitExpires: "2029-04-12",
    consents: { transactional: true, marketing: true },
  },
  {
    id: "c-2",
    name: "Fake Name Two",
    phone: "(612) 555-0202",
    email: "two@example.test",
    address: "202 Sample Ave, Minneapolis, MN 55401",
    hasPermit: null,
    consents: { transactional: true, marketing: false },
  },
  {
    id: "c-3",
    name: "Fake Name Three",
    phone: "(651) 555-0303",
    email: "three@example.test",
    address: "303 Mock Rd, Saint Paul, MN 55101",
    hasPermit: "transferee",
    permitExpires: "2027-08-30",
    consents: { transactional: true, marketing: true },
  },
  {
    id: "c-4",
    name: "Fake Name Four",
    phone: "(763) 555-0404",
    email: "four@example.test",
    address: "404 Test Ln, Plymouth, MN 55441",
    hasPermit: null,
    consents: { transactional: false, marketing: false },
  },
];

export type Item = {
  id: string;
  sku: string;
  name: string;
  priceCents: number;
  category: "firearm" | "ammo" | "accessory" | "service" | "nfa";
  ageClass?: 18 | 21 | null;
  taxable: boolean;
};

export const items: Item[] = [
  {
    id: "i-1",
    sku: "FM-GLK19-G5",
    name: "Glock 19 Gen 5",
    priceCents: 59900,
    category: "firearm",
    ageClass: 21,
    taxable: true,
  },
  {
    id: "i-2",
    sku: "FM-SW-SHIELD",
    name: "S&W M&P Shield Plus 9mm",
    priceCents: 44900,
    category: "firearm",
    ageClass: 21,
    taxable: true,
  },
  {
    id: "i-3",
    sku: "FM-RUG-1022",
    name: "Ruger 10/22 Carbine",
    priceCents: 32900,
    category: "firearm",
    ageClass: 18,
    taxable: true,
  },
  {
    id: "i-4",
    sku: "AM-9MM-124",
    name: "Federal 9mm 124gr FMJ (50)",
    priceCents: 2499,
    category: "ammo",
    ageClass: 21,
    taxable: true,
  },
  {
    id: "i-5",
    sku: "AM-556-M855",
    name: "PMC 5.56 M855 (20)",
    priceCents: 1699,
    category: "ammo",
    ageClass: 18,
    taxable: true,
  },
  {
    id: "i-6",
    sku: "AC-HOLSTER",
    name: "Vedder LightTuck holster",
    priceCents: 7999,
    category: "accessory",
    taxable: true,
  },
  {
    id: "i-7",
    sku: "AC-GUNLOCK",
    name: "Cable gun lock",
    priceCents: 1499,
    category: "accessory",
    taxable: true,
  },
  {
    id: "i-8",
    sku: "SVC-BG-CHECK",
    name: "Background check fee",
    priceCents: 2500,
    category: "service",
    taxable: false,
  },
  {
    id: "i-9",
    sku: "SVC-TRANSFER",
    name: "FFL transfer fee",
    priceCents: 3500,
    category: "service",
    taxable: false,
  },
  {
    id: "i-10",
    sku: "AC-RANGE-BAG",
    name: "Range bag",
    priceCents: 4999,
    category: "accessory",
    taxable: true,
  },
];

export type FirearmUnit = {
  id: string;
  serial: string;
  make: string;
  model: string;
  caliber: string;
  type: "pistol" | "rifle" | "shotgun" | "sbr" | "nfa-suppressor";
  mnCategory: "pistol" | "SMSAW" | "long-gun" | "other";
  status:
    | "in stock"
    | "held for customer"
    | "sold awaiting pickup"
    | "nfa pending"
    | "out for service"
    | "customer property";
  acquiredAt: string;
};

export const firearmUnits: FirearmUnit[] = [
  {
    id: "fu-1",
    serial: "BXYZ123",
    make: "Glock",
    model: "19 Gen 5",
    caliber: "9mm",
    type: "pistol",
    mnCategory: "pistol",
    status: "in stock",
    acquiredAt: "2026-09-14",
  },
  {
    id: "fu-2",
    serial: "SW88820",
    make: "Smith & Wesson",
    model: "M&P Shield Plus",
    caliber: "9mm",
    type: "pistol",
    mnCategory: "pistol",
    status: "sold awaiting pickup",
    acquiredAt: "2026-07-02",
  },
  {
    id: "fu-3",
    serial: "RG-440019",
    make: "Ruger",
    model: "10/22 Carbine",
    caliber: ".22 LR",
    type: "rifle",
    mnCategory: "long-gun",
    status: "in stock",
    acquiredAt: "2026-10-01",
  },
  {
    id: "fu-4",
    serial: "SIG-P365-443",
    make: "Sig Sauer",
    model: "P365",
    caliber: "9mm",
    type: "pistol",
    mnCategory: "pistol",
    status: "held for customer",
    acquiredAt: "2026-10-11",
  },
  {
    id: "fu-5",
    serial: "RUG-PC9-991",
    make: "Ruger",
    model: "PC Carbine",
    caliber: "9mm",
    type: "rifle",
    mnCategory: "long-gun",
    status: "customer property",
    acquiredAt: "2026-10-15",
  },
  {
    id: "fu-6",
    serial: "DD-SPC-77",
    make: "Dead Air",
    model: "Sandman-S",
    caliber: ".30 cal",
    type: "nfa-suppressor",
    mnCategory: "other",
    status: "nfa pending",
    acquiredAt: "2026-08-20",
  },
];

export type OpenTicket = {
  id: string;
  label: string;
  phone?: string;
  email?: string;
  salesperson: string;
  openedBy: string;
  device: string;
  linesSummary: string;
  totalCents: number;
  status:
    | "cart"
    | "parked"
    | "waiting on buyer"
    | "nics delayed"
    | "sent to counter"
    | "ready for pickup";
  lastActivity: string;
  beingEditedBy?: string | null;
};

export const openTickets: OpenTicket[] = [
  {
    id: "t-1001",
    label: "Fake Name One",
    phone: "(763) 555-0100",
    email: "one@example.test",
    salesperson: "s-jake",
    openedBy: "s-jake",
    device: "Floor iPad 2",
    linesSummary: "Glock 19 Gen 5 · bg check · transfer fee",
    totalCents: 65900,
    status: "cart",
    lastActivity: "2 min ago",
    beingEditedBy: "s-jake",
  },
  {
    id: "t-1002",
    label: "Fake Name Two — on hold",
    phone: "(612) 555-0202",
    email: "two@example.test",
    salesperson: "s-sarah",
    openedBy: "s-sarah",
    device: "Counter 2",
    linesSummary: "S&W Shield Plus · bg check",
    totalCents: 47400,
    status: "nics delayed",
    lastActivity: "3 h ago",
    beingEditedBy: null,
  },
  {
    id: "t-1003",
    label: "Walk-in · ammo",
    salesperson: "s-sarah",
    openedBy: "s-sarah",
    device: "Floor iPad 1",
    linesSummary: "9mm 124gr × 4",
    totalCents: 9996,
    status: "sent to counter",
    lastActivity: "just now",
    beingEditedBy: null,
  },
  {
    id: "t-1004",
    label: "Fake Name Three — transfer pickup",
    phone: "(651) 555-0303",
    email: "three@example.test",
    salesperson: "s-jake",
    openedBy: "s-max",
    device: "Receiving PC",
    linesSummary: "Sig P365 (transfer in) · transfer fee · bg check",
    totalCents: 10000,
    status: "ready for pickup",
    lastActivity: "yesterday",
    beingEditedBy: null,
  },
  {
    id: "t-1005",
    label: "Fake Name Four — parked",
    phone: "(763) 555-0404",
    email: "four@example.test",
    salesperson: "s-sarah",
    openedBy: "s-sarah",
    device: "Floor iPad 1",
    linesSummary: "Range bag · holster",
    totalCents: 12998,
    status: "parked",
    lastActivity: "20 min ago",
    beingEditedBy: null,
  },
];

export type { TodayCard } from "./fixtures-today";
export { todayBoard } from "./fixtures-today";

export function fmtMoney(cents: number): string {
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function staffById(id: string): Staff | undefined {
  return staff.find((s) => s.id === id);
}
